import { ExportSheet } from '@/@studio-ai/types';

/**
 * Triggers a file download in the browser using a Blob URL.
 * Creates a temporary anchor element, clicks it, then cleans up.
 */
function triggerDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

/**
 * Checks if the provided Markdown string contains a valid table that can be exported.
 */
export function canExportTable(tableMarkdown?: string) {
    if (!tableMarkdown) return false;
    const tableRows = extractTableData(tableMarkdown);
    return tableRows.length > 1; // At least a header and one data row
}

/**
 * Extracts table data from a Markdown string, ignoring separator rows.
 */
function extractTableData(tableMarkdown: string) {
    const lines = tableMarkdown.trim().split('\n');
    const dataLines = lines.filter((line) => {
        const trimmed = line.trim();
        if (!trimmed.startsWith('|')) return false;
        return !/^\|[\s|:\-]+\|$/.test(trimmed);
    });
    const tableRows = dataLines.map((line) =>
        line
            .trim()
            .slice(1, -1)
            .split('|')
            .map((cell) => cell.trim()),
    );
    return tableRows;
}

/**
 * Builds an XLSX workbook from one or more sheets of { columns, rows } data
 * and triggers a browser download. Handles duplicate sheet names and
 * Excel's 31-character sheet name limit.
 */
export async function exportSheetsToXlsx(sheets: ExportSheet[], filename: string): Promise<void> {
    const ExcelJS = (await import('exceljs')).default;
    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'MFour Studio';

    const nonEmptySheets = sheets.filter((s) => s.columns.length > 0 || s.rows.length > 0);
    if (nonEmptySheets.length === 0) return;

    const usedNames = new Set<string>();
    for (let idx = 0; idx < nonEmptySheets.length; idx++) {
        const sheet = nonEmptySheets[idx];
        const baseName = (sheet.name || 'Data').slice(0, 28);
        const truncatedBase = baseName.slice(0, 25);
        let sheetName =
            nonEmptySheets.length === 1 ? baseName : `${truncatedBase} (${idx + 1})`.slice(0, 31);
        let dup = idx + 1;
        while (usedNames.has(sheetName)) {
            dup += 1;
            sheetName = `${truncatedBase} (${dup})`.slice(0, 31);
        }
        usedNames.add(sheetName);

        const worksheet = workbook.addWorksheet(sheetName);

        const headerRow = worksheet.addRow(sheet.columns);
        headerRow.font = { bold: true };

        for (const row of sheet.rows) {
            worksheet.addRow(row);
        }

        worksheet.columns.forEach((column) => {
            column.width = 20;
        });
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    triggerDownload(blob, filename);
}

/**
 * Exports a Markdown table to an XLSX file.
 * Parses the markdown into columns + rows and delegates to exportSheetsToXlsx.
 */
export async function exportTableToXlsx(tableMarkdown: string, title: string): Promise<void> {
    const tableRows = extractTableData(tableMarkdown);
    if (tableRows.length === 0) return;

    const columns = tableRows[0];
    const rows = tableRows.slice(1);

    await exportSheetsToXlsx([{ name: title, columns, rows }], `${title}.xlsx`);
}

/**
 * Exports table data to a CSV file.
 *
 * Column names become the header row. Values containing commas, double-quotes,
 * or newlines are wrapped in double-quotes (RFC 4180 compliant).
 *
 * @param tableMarkdown - The Markdown string representing the table, including header and separator rows.
 *                        Example:
 *                        | Name  | Age |
 *                        |-------|-----|
 *                        | Alice | 30  |
 *                        | Bob   | 25  |
 *
 * @param title   - Used as the downloaded filename (sans extension)
 */
export function exportTableToCsv(tableMarkdown: string, title: string): void {
    const tableRows = extractTableData(tableMarkdown);
    const csv = tableRows
        .map((row) =>
            row
                .map((cell) => {
                    if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
                        return `"${cell.replace(/"/g, '""')}"`;
                    }
                    return cell;
                })
                .join(','),
        )
        .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    triggerDownload(blob, `${title}.csv`);
}

/**
 * Serializes one sheet of { columns, rows } data to an RFC 4180 CSV string.
 *
 * Cells containing commas, double-quotes, or line breaks are wrapped in
 * double-quotes with embedded quotes doubled, so verbatim text (e.g. speech
 * transcripts) survives intact. Records are CRLF-separated and the output is
 * prefixed with a UTF-8 BOM so Excel decodes non-ASCII text correctly. Cells
 * starting with a formula trigger (= + @ or tab) get a leading apostrophe —
 * same mitigation as `sanitizeCSVCell` in `src/@admin/utils/reportHelpers.ts`,
 * minus `-`, which legitimately starts verbatim speech.
 */
export function serializeSheetToCsv(sheet: ExportSheet): string {
    const escapeCell = (raw: unknown): string => {
        const value = String(raw ?? '');
        const guarded = /^[=+@\t]/.test(value) ? `'${value}` : value;
        return /[",\r\n]/.test(guarded) ? `"${guarded.replace(/"/g, '""')}"` : guarded;
    };
    const records = [sheet.columns, ...sheet.rows].map((row) => row.map(escapeCell).join(','));
    return `\uFEFF${records.join('\r\n')}\r\n`;
}

/**
 * Serializes one sheet to CSV (see serializeSheetToCsv) and triggers a browser
 * download of the result.
 */
export function exportSheetToCsv(sheet: ExportSheet, filename: string): void {
    const blob = new Blob([serializeSheetToCsv(sheet)], { type: 'text/csv;charset=utf-8;' });
    triggerDownload(blob, filename);
}

/** One HTML table for the clipboard: Excel and Sheets paste it cell for cell. */
export function tableRowsToHtml(rows: string[][]): string {
    const escape = (value: string) =>
        value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const [header = [], ...body] = rows;
    const headerHtml = `<tr>${header.map((cell) => `<th>${escape(cell)}</th>`).join('')}</tr>`;
    const bodyHtml = body
        .map((row) => `<tr>${row.map((cell) => `<td>${escape(cell)}</td>`).join('')}</tr>`)
        .join('');
    return `<table>${headerHtml}${bodyHtml}</table>`;
}

/**
 * Copies a DANI table to the clipboard as an HTML table with tab-separated text beside it.
 * Excel reads the HTML and keeps every column, where pasting the text alone could land in
 * one column (BT-1945). Browsers without `ClipboardItem` get the text only. Throws when the
 * clipboard refuses, so the caller can say the copy failed.
 */
export async function copyTableToClipboard(tableMarkdown: string): Promise<void> {
    const tableRows = extractTableData(tableMarkdown);
    const tsv = tableRows.map((row) => row.join('\t')).join('\n');
    if (typeof ClipboardItem === 'undefined' || !navigator.clipboard.write) {
        await navigator.clipboard.writeText(tsv);
        return;
    }
    await navigator.clipboard.write([
        new ClipboardItem({
            'text/html': new Blob([tableRowsToHtml(tableRows)], { type: 'text/html' }),
            'text/plain': new Blob([tsv], { type: 'text/plain' }),
        }),
    ]);
}
